import { useState, type ReactNode } from 'react';

import { TimeChart, type ChartSeries } from '../components/TimeChart.tsx';
import { compactUsd, tone } from '../components/ui.tsx';
import { usePoll } from '../hooks/usePoll.ts';
import { fmtUnits } from '../lib/format.ts';
import { relayer, type AuctionEpoch, type StatsRange, type StatsSample } from '../lib/relayer.ts';
import { count, cumulative, money, signedMoney, units } from '../lib/stats.ts';
import { useApp } from '../state.tsx';

const RANGES: { id: StatsRange; label: string }[] = [
  { id: '7d', label: '7D' },
  { id: '30d', label: '30D' },
  { id: '90d', label: '90D' },
  { id: 'all', label: 'All' },
];
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
const SERIES_REFRESH_MS = 60_000;
const EPOCHS_REFRESH_MS = 120_000;
const EPOCHS_SHOWN = 20;
/** The auction's reserve tick of an epoch that took nothing */
const NO_TICK = 2 ** 32 - 1;

const dash = '- -';
const papurr = units('PAPURR');
const hype = units('HYPE');
const signedUsdWad = (v: bigint) => `${v < 0n ? '-' : '+'}${compactUsd(v < 0n ? -v : v)}`;
const NO_TRADES = 'No trades yet: this chart starts with the first one';
const NO_SAMPLES = 'Sampled every hour from now on';

/**
 * The protocol's history in charts: trading, the house, PAPURR and the flows over a range, and the burn
 * auction epoch by epoch. Everything comes from the API's stats series (events and hourly samples).
 */
export function StatsPage() {
  const { stats, globals, auction, reserve } = useApp();
  const [range, setRange] = useState<StatsRange>('30d');
  const s = usePoll(() => relayer.series(range), SERIES_REFRESH_MS, [range]);
  const ep = usePoll(() => relayer.epochs(), EPOCHS_REFRESH_MS, []);

  const rows = s.data?.rows ?? [];
  const samples = s.data?.samples ?? [];
  const bucketMs = s.data?.bucketMs ?? DAY_MS;
  const sampleMs = range === '7d' || range === '30d' ? HOUR_MS : DAY_MS;
  const times = rows.map((r) => r.t);
  const st = samples.map((x) => x.t);
  // USD per core wei, for the reserve's HYPE-side holdings
  const coreScale = globals ? Number(globals.coreScale) / 1e18 : 0;
  const of = (pick: (x: StatsSample) => number | undefined) => samples.map(pick);
  const houseResult = rows.map((r) => -r.tradersPnl - r.fees);

  const all = stats?.activityAll;
  const houseAll = all ? -all.tradersPnl - all.fees : undefined;
  const tvlNow =
    globals !== undefined
      ? (globals.poolEvm ?? 0n) + (globals.poolCore ?? 0n) + globals.houseLent + (reserve ? reserve.totalWei * globals.coreScale : 0n)
      : undefined;

  return (
    <div className="stats-page">
      <div className="stats-head">
        <div>
          <h1>Stats</h1>
          <p className="desc">The protocol since launch, from its onchain events and an hourly reading of its state. Nothing here is estimated.</p>
        </div>
        <div className="seg seg-sm" role="tablist" aria-label="Range">
          {RANGES.map((r) => (
            <button key={r.id} role="tab" aria-selected={range === r.id} className={range === r.id ? 'is-on' : ''} onClick={() => setRange(r.id)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="stats-kpis">
        <Kpi label="Volume, all time" value={all ? compactUsd(all.volume) : dash} sub={all ? `${all.opened.toLocaleString()} positions opened` : undefined} />
        <Kpi label="Trades closed" value={all ? all.closed.toLocaleString() : dash} sub={all && all.closed ? `${Math.round((all.wins / all.closed) * 100)}% won · ${all.liquidations.toLocaleString()} liquidated` : undefined} />
        <Kpi label="Traders" value={stats ? stats.traders.toLocaleString() : dash} sub={stats ? `${stats.openPositions.toLocaleString()} positions open now` : undefined} />
        <Kpi label="House result, all time" value={houseAll !== undefined ? signedUsdWad(houseAll) : dash} tone={tone(houseAll)} sub={all ? `${compactUsd(all.fees)} of fees to PAPURR & protocol` : undefined} />
        <Kpi
          label="PAPURR supply"
          value={globals ? fmtUnits(globals.papurrSupply, 18, 0) : dash}
          sub={auction ? `${fmtUnits(auction.totalBurnedWei, 18, 0)} burned by the auction` : globals ? `${fmtUnits(globals.totalStaked, 18, 0)} staked` : undefined}
        />
        <Kpi label="Value locked" value={tvlNow !== undefined ? compactUsd(tvlNow) : dash} sub={globals ? `house ${compactUsd(globals.effectiveLp > 0n ? globals.effectiveLp : 0n)}` : undefined} />
      </div>

      {s.error && !s.data ? (
        <section className="panel">
          <div className="empty">{`Stats unavailable (${s.error})`}</div>
        </section>
      ) : !s.data ? (
        <section className="panel">
          <div className="empty">Loading…</div>
        </section>
      ) : (
        <>
          <h2 className="stats-section">Trading</h2>
          <div className="stats-grid">
            <Card title="Volume" lead="Notional opened (margin times leverage), per hour over a week and per day beyond.">
              <TimeChart
                times={times}
                bucketMs={bucketMs}
                format={money}
                empty={NO_TRADES}
                series={[
                  { id: 'vol', label: 'Volume', kind: 'bar', values: rows.map((r) => r.volume) },
                  { id: 'opened', label: 'Positions opened', kind: 'bar', hidden: true, values: rows.map((r) => r.opened), format: count },
                ]}
              />
            </Card>
            <Card title="Traders" lead="Wallets that opened or closed a position, and how many traded for the first time, added up.">
              <TimeChart
                times={times}
                bucketMs={bucketMs}
                format={count}
                empty={NO_TRADES}
                series={[
                  { id: 'active', label: 'Active', kind: 'bar', values: rows.map((r) => r.traders) },
                  { id: 'newcum', label: 'New, cumulative', kind: 'line', tone: 'ink', values: cumulative(rows.map((r) => r.newTraders)) },
                  { id: 'new', label: 'New', kind: 'bar', hidden: true, values: rows.map((r) => r.newTraders) },
                ]}
              />
            </Card>
            <Card title="Outcomes" lead="Closed positions by how they ended: a win, a loss closed by the trader, or a liquidation.">
              <TimeChart
                times={times}
                bucketMs={bucketMs}
                format={count}
                empty={NO_TRADES}
                series={[
                  { id: 'wins', label: 'Wins', kind: 'bar', tone: 'pos', values: rows.map((r) => r.wins) },
                  { id: 'losses', label: 'Losses', kind: 'bar', tone: 'muted', values: rows.map((r) => Math.max(0, r.closed - r.wins - r.liquidations)) },
                  { id: 'liq', label: 'Liquidations', kind: 'bar', tone: 'neg', values: rows.map((r) => r.liquidations) },
                ]}
              />
            </Card>
            <Card title="Fees" lead="Taken on closes and liquidations, shared between stakers and the protocol.">
              <TimeChart
                times={times}
                bucketMs={bucketMs}
                format={money}
                empty={NO_TRADES}
                series={[
                  { id: 'fees', label: 'Fees', kind: 'bar', tone: 'warn', values: rows.map((r) => r.fees) },
                  { id: 'feescum', label: 'Cumulative', kind: 'line', tone: 'ink', values: cumulative(rows.map((r) => r.fees)) },
                ]}
              />
            </Card>
          </div>

          <h2 className="stats-section">The house</h2>
          <div className="stats-grid">
            <Card title="House result" lead="What the house kept or paid out on the closes of the period, fees included, added up. Green: traders lost on balance.">
              <TimeChart
                times={times}
                bucketMs={bucketMs}
                format={signedMoney}
                empty={NO_TRADES}
                series={[
                  { id: 'cum', label: 'Cumulative', kind: 'pnl', values: cumulative(houseResult) },
                  { id: 'per', label: 'This period', kind: 'bar', hidden: true, values: houseResult },
                  { id: 'tpnl', label: "Traders' net result", kind: 'bar', hidden: true, values: rows.map((r) => r.tradersPnl) },
                ]}
              />
            </Card>
            <Card title="House funds" lead="What the house holds, the part of it lent on HyperCore, and the wins waiting in the queue.">
              <TimeChart
                times={st}
                bucketMs={sampleMs}
                format={money}
                empty={NO_SAMPLES}
                series={[
                  { id: 'lp', label: 'House', kind: 'area', values: of((x) => x.effectiveLp) },
                  { id: 'lent', label: 'Lent on HyperCore', kind: 'line', tone: 'muted', values: of((x) => x.houseLent) },
                  { id: 'queued', label: 'Wins waiting', kind: 'line', tone: 'neg', values: of((x) => x.queued) },
                ]}
              />
            </Card>
            <Card title="Open interest" lead="Longs and shorts open against the house, at the price of the hour.">
              <TimeChart
                times={st}
                bucketMs={sampleMs}
                format={money}
                empty={NO_SAMPLES}
                series={[
                  { id: 'long', label: 'Longs', kind: 'line', tone: 'pos', values: of((x) => x.oiLong) },
                  { id: 'short', label: 'Shorts', kind: 'line', tone: 'neg', values: of((x) => x.oiShort) },
                  { id: 'open', label: 'Positions open', kind: 'line', hidden: true, values: of((x) => x.openPositions), format: count },
                ]}
              />
            </Card>
            <Card title="Deposits and withdrawals" lead="USDC traders brought in and took out, from HyperCore, HyperEVM and other chains.">
              <TimeChart
                times={times}
                bucketMs={bucketMs}
                format={money}
                empty="No transfer yet"
                series={[
                  { id: 'dep', label: 'Deposits', kind: 'bar', tone: 'pos', values: rows.map((r) => r.deposits) },
                  { id: 'wd', label: 'Withdrawals', kind: 'bar', tone: 'neg', negate: true, values: rows.map((r) => r.withdrawals) },
                  { id: 'net', label: 'Net', kind: 'line', hidden: true, values: rows.map((r) => r.deposits - r.withdrawals), format: signedMoney },
                ]}
              />
            </Card>
            <Card title="Value locked" lead="USDC in the exchange for traders and the house, what the house lends on HyperCore, and the reserve.">
              <TimeChart
                times={st}
                bucketMs={sampleMs}
                format={money}
                empty={NO_SAMPLES}
                series={[
                  { id: 'tvl', label: 'Total', kind: 'area', values: of((x) => x.poolEvm + x.poolCore + x.houseLent + Number(x.reserveWei) * coreScale) },
                  { id: 'reserve', label: 'Reserve', kind: 'line', tone: 'muted', values: of((x) => Number(x.reserveWei) * coreScale) },
                ]}
              />
            </Card>
            <Card title="PAPURR revenue" lead="Fees and the house's shared gains sent to the HYPE buyback, and the HYPE it bought with them, split between the stakers' stream and the burn auction.">
              <TimeChart
                times={times}
                bucketMs={bucketMs}
                format={money}
                empty="No distribution yet"
                series={[
                  { id: 'dist', label: 'Distributed', kind: 'bar', tone: 'warn', values: rows.map((r) => r.distributed) },
                  { id: 'hype', label: 'HYPE bought', kind: 'bar', hidden: true, values: rows.map((r) => r.hypeBought), format: hype },
                ]}
              />
            </Card>
          </div>

          <h2 className="stats-section">PAPURR</h2>
          <div className="stats-grid">
            <Card title="Supply" lead="PAPURR in existence, how much of it is staked, and what the burn auction has taken out for good.">
              <TimeChart
                times={st}
                bucketMs={sampleMs}
                format={papurr}
                empty={NO_SAMPLES}
                series={[
                  { id: 'supply', label: 'Supply', kind: 'area', values: of((x) => x.papurrSupply) },
                  { id: 'staked', label: 'Staked', kind: 'line', tone: 'muted', values: of((x) => x.totalStaked) },
                  { id: 'burned', label: 'Burned, cumulative', kind: 'line', tone: 'neg', values: of((x) => x.totalBurned) },
                ]}
              />
            </Card>
            <Card title="Minted and burned" lead="PAPURR minted to losers, and PAPURR the auction burned, per period.">
              <TimeChart
                times={times}
                bucketMs={bucketMs}
                format={papurr}
                empty={NO_TRADES}
                series={[
                  { id: 'minted', label: 'Minted', kind: 'bar', values: rows.map((r) => r.minted) },
                  { id: 'burn', label: 'Burned', kind: 'bar', tone: 'neg', negate: true, values: rows.map((r) => r.burned) },
                ]}
              />
            </Card>
          </div>

          <h2 className="stats-section">Burn auction</h2>
          <Epochs epochs={ep.data} error={ep.error} />
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone: t }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="stats-kpi">
      <span>{label}</span>
      <b className={`num ${t ?? ''}`}>{value}</b>
      {sub && <small>{sub}</small>}
    </div>
  );
}

function Card({ title, lead, children, wide }: { title: string; lead: string; children: ReactNode; wide?: boolean }) {
  return (
    <section className={`stats-card ${wide ? 'wide' : ''}`}>
      <h3>{title}</h3>
      <p className="lead">{lead}</p>
      {children}
    </section>
  );
}

/** The closed epochs: a chart of what each took and burned, and the table behind it */
function Epochs({ epochs, error }: { epochs?: AuctionEpoch[]; error?: string }) {
  if (error && !epochs) return <section className="panel"><div className="empty">{`Epochs unavailable (${error})`}</div></section>;
  if (!epochs) return <section className="panel"><div className="empty">Loading…</div></section>;
  const asc = [...epochs].reverse();
  const price = (e: AuctionEpoch) => (e.cleared > 0 ? e.pot / e.cleared : undefined);
  const series: ChartSeries[] = [
    { id: 'taken', label: 'Taken', kind: 'bar', values: asc.map((e) => e.cleared) },
    { id: 'burned', label: 'Burned', kind: 'bar', tone: 'neg', negate: true, values: asc.map((e) => e.burned) },
    { id: 'resold', label: 'Bought back', kind: 'bar', hidden: true, values: asc.map((e) => e.resold) },
    { id: 'price', label: 'Price', kind: 'line', hidden: true, values: asc.map(price), format: (v) => `${v.toFixed(v >= 1 ? 2 : 6)} HYPE` },
  ];
  return (
    <div className="stats-grid">
      <Card title="Epoch by epoch" lead="PAPURR each epoch took at its clearing price, and how much of it was burned once the resale window closed.">
        <TimeChart times={asc.map((e) => e.closedAt)} bucketMs={DAY_MS} format={papurr} empty="No epoch has closed yet" series={series} />
      </Card>
      <Card title="Closed epochs" lead="Newest first. The price is the HYPE paid per PAPURR taken; what is not bought back during the next epoch burns.">
        {epochs.length === 0 ? (
          <div className="empty">No epoch has closed yet</div>
        ) : (
          <div className="table-wrap">
            <table className="grid-table stats-epochs">
              <thead>
                <tr>
                  <th>Epoch</th>
                  <th>Closed</th>
                  <th className="r">Pot</th>
                  <th className="r">Offered</th>
                  <th className="r">Taken</th>
                  <th className="r">Price</th>
                  <th className="r">Bought back</th>
                  <th className="r">Burned</th>
                </tr>
              </thead>
              <tbody>
                {epochs.slice(0, EPOCHS_SHOWN).map((e) => {
                  const p = price(e);
                  return (
                    <tr key={e.epoch}>
                      <td className="num">#{e.epoch}</td>
                      <td className="num">{new Date(e.closedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</td>
                      <td className="r num">{hype(e.pot)}</td>
                      <td className="r num">{papurr(e.sold)}</td>
                      <td className="r num">{e.cutoff === NO_TICK || e.cleared === 0 ? dash : papurr(e.cleared)}</td>
                      <td className="r num">{p === undefined ? dash : `${p.toFixed(p >= 1 ? 2 : 6)} HYPE`}</td>
                      <td className="r num">{e.resold > 0 ? papurr(e.resold) : dash}</td>
                      <td className={`r num ${e.burned > 0 ? 'neg' : ''}`}>{e.burned > 0 ? papurr(e.burned) : dash}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
